import { createReducer, on, Action } from '@ngrx/store';
import {
	ClearingUniversityProfile,
	UniversityProfile,
	UniversityRatingStats,
} from '@uc/web/shared/data-models';
import * as StateUniversityActions from './university.actions';
import { updateSavedInStore } from '@uc/web/shared/features/feature-bookmark';

export const _STATE_UNIVERSITY_FEATURE_KEY = 'stateUniversity';

export interface State {
	university: UniversityProfile | null;
	clearingUniversity: ClearingUniversityProfile | null;
	universityRatingStats: UniversityRatingStats | null;
	isLoadMoreUniversityReviews?: boolean;
	universityUrl?: string;
	isLoading: boolean; // has the University record been loaded
	error: any;
}

export interface StateUniversityPartialState {
	readonly [_STATE_UNIVERSITY_FEATURE_KEY]: State;
}

export const initialState: State = {
	// set initial required properties
	university: null,
	clearingUniversity: null,
	universityRatingStats: null,
	isLoading: false,
	error: null,
};

const stateUniversityReducer = createReducer(
	initialState,
	on(
		StateUniversityActions.fetchUniversity,
		StateUniversityActions.fetchClearingUniversity,
		StateUniversityActions.fetchUniversityRatingStats,
		StateUniversityActions.fetchUniByProduct,
		(state) => ({
			...state,
			isLoading: true,
			error: null,
		}),
	),
	on(StateUniversityActions.loadStateUniversityFailure, (state, error) => ({
		...state,
		isLoading: false,
		error,
	})),
	/**
	 * on university fetch success, update the state
	 */
	on(StateUniversityActions.updateUniversity, (state, stateUniversity) => ({
		...state,
		isLoading: false,
		error: null,
		university: stateUniversity,
		selectedId: stateUniversity.id,
	})),
	on(StateUniversityActions.updateClearingUniversity, (state, stateUniversity) => ({
		...state,
		isLoading: false,
		error: null,
		clearingUniversity: stateUniversity,
		selectedId: stateUniversity.id,
	})),
	/**
	 * when user navigates between UG and PG
	 * update the state with data that is different for each application type
	 */
	on(StateUniversityActions.updateUniByProduct, (state, stateProductBasedData) => {
		return {
			...state,
			isLoading: false,
			error: null,
			university: {
				...state.university,
				overview: stateProductBasedData.overview,
				universityGallery: stateProductBasedData.universityGallery,
			} as UniversityProfile,
		};
	}),
	on(StateUniversityActions.updateUniversityRatingStats, (state, stateUniversity) => ({
		...state,
		isLoading: false,
		universityRatingStats: stateUniversity,
	})),
	on(updateSavedInStore, (state, { uniId }) => ({
		...state,
		university: updateSavedProperty<UniversityProfile>(state.university, uniId),
		clearingUniversity: updateSavedProperty<ClearingUniversityProfile>(
			state.clearingUniversity,
			uniId,
		),
	})),
	on(StateUniversityActions.updateUniversityUrl, (state, stateUniversityUrl) => ({
		...state,
		universityUrl: stateUniversityUrl.universityUrl,
	})),
	on(
		StateUniversityActions.loadMoreUniversityReviews,
		(state, stateLoadMoreUniversityReviews) => ({
			...state,
			isLoadMoreUniversityReviews: stateLoadMoreUniversityReviews.isLoadMore,
		}),
	),
);

export function reducer(state: State | undefined, action: Action) {
	return stateUniversityReducer(state, action);
}

function updateSavedProperty<T extends { id: number; saved: boolean }>(
	university: T | null,
	uniId: number,
): T | null {
	return university && university.id === uniId
		? { ...university, saved: !university.saved }
		: university;
}
